import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Custom hook for signature validation
 * Handles the link signature verification logic
 * 
 * @returns {Object} - { isValidating, isLinkValid, params }
 */
export const useSignatureValidation = () => {
    const [isValidating, setIsValidating] = useState(true);
    const [isLinkValid, setIsLinkValid] = useState(true);
    const location = useLocation();
    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    useEffect(() => {
        const validateSignature = async () => {
            try {
                // Parse the query string
                const params = new URLSearchParams(location.search);
                
                // Get signature parameters
                const expires_at = params.get('expires_at');
                const signature = params.get('signature');
                
                // If both signature parameters are present, validate them
                if (expires_at && signature) {
                    console.log('Found signature parameters, validating...');
                    
                    try {
                        const verifyResponse = await fetch(
                            `${backendUrl}/api/v1/signature/verify?expires_at=${encodeURIComponent(expires_at)}&signature=${encodeURIComponent(signature)}`
                        );
                        
                        if (!verifyResponse.ok) {
                            throw new Error('Verification request failed');
                        }
                        
                        const verifyData = await verifyResponse.json();
                        
                        if (!verifyData.valid) {
                            console.log('Link validation failed:', verifyData.reason);
                            setIsLinkValid(false);
                            setIsValidating(false);
                            return;
                        }
                        
                        console.log('Link validation successful');
                    } catch (error) {
                        console.error('Error validating link:', error);
                        setIsLinkValid(false);
                        setIsValidating(false);
                        return;
                    }
                } else if (expires_at || signature) {
                    // If only one parameter is present, this is suspicious
                    console.log('Incomplete signature parameters');
                    setIsLinkValid(false);
                    setIsValidating(false);
                    return;
                } else {
                    // No signature parameters - allow for backward compatibility
                    console.log('No signature parameters found, proceeding for backward compatibility');
                }
                
                // If we reach here, the link is valid or no signature was provided
                setIsValidating(false);
                
            } catch (error) {
                console.error('Error in signature validation process:', error);
                setIsLinkValid(false);
                setIsValidating(false);
            }
        };

        validateSignature();
    }, [location.search, backendUrl]);

    return {
        isValidating,
        isLinkValid,
        params: new URLSearchParams(location.search)
    };
};